import axios from "axios";
import swal from "sweetalert";
import { formatCurrency } from "./numberFormatter";

// Sums the selected rows per currency, since a selection can mix currencies.
const buildTotalsContent = (rows, getAmount, getCurrencyName) => {
  const totalsByCurrency = rows.reduce((acc, row) => {
    const currencyName = getCurrencyName(row) || "PHP";
    acc[currencyName] =
      (acc[currencyName] || 0) + (parseFloat(getAmount(row)) || 0);
    return acc;
  }, {});

  const content = document.createElement("div");
  content.innerHTML = `
    <div style="margin-bottom: 4px;">Total Amount:</div>
    ${Object.entries(totalsByCurrency)
      .map(
        ([currencyName, total]) =>
          `<div style="font-size: 1.3rem; font-weight: bold;">${formatCurrency(total, currencyName)}</div>`,
      )
      .join("")}`;
  return content;
};

const buildSkippedContent = (skipped) => {
  const content = document.createElement("div");
  content.innerHTML = `
    <div style="margin-bottom: 8px;">Skipped ${skipped.length}:</div>
    <div style="text-align: left; max-height: 200px; overflow-y: auto;">
      ${skipped
        .map(
          (s) =>
            `<div><strong>${s.transaction_id}</strong> — ${s.reason}</div>`,
        )
        .join("")}
    </div>`;
  return content;
};

// Confirm → POST { ids, userLoggedID } → show result, for the bulk
// Approve All / Reject All buttons on the Pending tabs.
//
// - url:             the module's /bulk-approved or /bulk-rejected endpoint
// - getId(row)       → id sent to the backend
// - getAmount(row)   → amount shown in the confirm total
// - getCurrencyName(row) → currency of that amount
// - onDone()         → refresh table / widgets / pending count
export const runBulkApproval = ({
  action,
  rows,
  url,
  userLoggedID,
  getId,
  getAmount,
  getCurrencyName,
  setIsProcessing = () => {},
  onDone = () => {},
}) => {
  const isApprove = action === "approve";
  const count = rows.length;

  swal({
    title: `${isApprove ? "Approve" : "Reject"} ${count} selected request${count > 1 ? "s" : ""}?`,
    content: buildTotalsContent(rows, getAmount, getCurrencyName),
    icon: "warning",
    buttons: true,
    dangerMode: true,
  }).then(async (confirmed) => {
    if (!confirmed) return;

    setIsProcessing(true);
    try {
      const res = await axios.post(url, {
        ids: rows.map(getId),
        userLoggedID,
      });

      const { processedCount = 0, skipped = [] } = res.data;
      const verb = isApprove ? "approved" : "rejected";

      swal({
        title: processedCount > 0 ? "Success" : "Nothing processed",
        text: `Successfully ${verb} ${processedCount} request${processedCount === 1 ? "" : "s"}`,
        icon: processedCount > 0 ? "success" : "warning",
        ...(skipped.length > 0 && { content: buildSkippedContent(skipped) }),
      }).then(onDone);
    } catch (error) {
      console.error(error);
      const { status, data } = error.response || {};
      if (status === 401) {
        // Imbalance checker — same payload as the single routes
        swal({ icon: "error", title: data.title, text: data.message });
      } else if (status === 422) {
        swal({
          icon: "error",
          title: "Approval Failed",
          text: data.message,
        });
      } else {
        swal({
          title: "Something went wrong",
          text: "Please contact your support immediately",
          icon: "error",
        });
      }
    } finally {
      setIsProcessing(false);
    }
  });
};
